import React, { useState } from 'react';
import riskService from '../../services/riskService';
import './RiskMatrix.css';

const RiskMatrix = ({ matrixData, onRiskClick }) => {
  const [selectedCell, setSelectedCell] = useState(null);

  if (!matrixData) {
    return (
      <div className="risk-matrix-container">
        <div className="text-center text-gray-500 py-8">
          No risk matrix data available
        </div>
      </div>
    );
  }

  const formattedData = riskService.formatMatrixData(matrixData);
  const impacts = ['Low', 'Medium', 'High', 'Critical'];

  const handleCellClick = (cell) => {
    setSelectedCell(cell);
  };

  const handleRiskItemClick = (risk) => {
    if (onRiskClick) {
      onRiskClick(risk);
    }
    setSelectedCell(null);
  };

  return (
    <div className="risk-matrix-container">
      <div className="risk-matrix-header">
        <h3 className="risk-matrix-title">Risk Matrix</h3>
        <p className="risk-matrix-subtitle">
          Probability vs Impact Analysis
        </p>
      </div>

      <div className="risk-matrix-wrapper">
        {/* Y-axis label */}
        <div className="risk-matrix-y-label">
          <span>Probability</span>
        </div>

        {/* Matrix grid */}
        <div className="risk-matrix-grid">
          {/* Column headers (Impact) */}
          <div className="risk-matrix-header-row">
            <div className="risk-matrix-corner-cell"></div>
            {impacts.map(impact => (
              <div key={impact} className="risk-matrix-header-cell">
                {impact}
              </div>
            ))}
          </div>

          {/* Matrix rows */}
          {formattedData.map(row => (
            <div key={row.probability} className="risk-matrix-row">
              {/* Row header (Probability) */}
              <div className="risk-matrix-row-header">
                {row.probability}
              </div>

              {/* Matrix cells */}
              {row.cells.map(cell => (
                <div
                  key={`${row.probability}-${cell.impact}`}
                  className={`risk-matrix-cell ${cell.color} ${cell.textColor} ${
                    cell.count > 0 ? 'has-risks' : ''
                  }`}
                  onClick={() => cell.count > 0 && handleCellClick(cell)}
                  title={`${row.probability} Probability, ${cell.impact} Impact\nRisk Score: ${cell.score}\nSeverity: ${cell.severity}\nRisks: ${cell.count}`}
                >
                  <div className="risk-matrix-cell-content">
                    <div className="risk-matrix-cell-score">{cell.score}</div>
                    {cell.count > 0 && (
                      <div className="risk-matrix-cell-count">
                        {cell.count} {cell.count === 1 ? 'risk' : 'risks'}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* X-axis label */}
        <div className="risk-matrix-x-label">
          <span>Impact</span>
        </div>
      </div>

      {/* Legend */}
      <div className="risk-matrix-legend">
        <div className="risk-matrix-legend-title">Severity Levels:</div>
        <div className="risk-matrix-legend-items">
          <div className="risk-matrix-legend-item">
            <span className="risk-matrix-legend-color bg-green-500"></span>
            <span>Low (1-3)</span>
          </div>
          <div className="risk-matrix-legend-item">
            <span className="risk-matrix-legend-color bg-yellow-500"></span>
            <span>Medium (4-6)</span>
          </div>
          <div className="risk-matrix-legend-item">
            <span className="risk-matrix-legend-color bg-orange-500"></span>
            <span>High (7-9)</span>
          </div>
          <div className="risk-matrix-legend-item">
            <span className="risk-matrix-legend-color bg-red-500"></span>
            <span>Critical (10-12)</span>
          </div>
        </div>
      </div>

      {/* Selected cell details modal */}
      {selectedCell && selectedCell.risks.length > 0 && (
        <div className="risk-matrix-modal-overlay" onClick={() => setSelectedCell(null)}>
          <div className="risk-matrix-modal" onClick={(e) => e.stopPropagation()}>
            <div className="risk-matrix-modal-header">
              <h4>
                {selectedCell.risks[0].probability} Probability, {selectedCell.risks[0].impact} Impact
              </h4>
              <button
                className="risk-matrix-modal-close"
                onClick={() => setSelectedCell(null)}
              >
                ×
              </button>
            </div>
            <div className="risk-matrix-modal-body">
              <div className="risk-matrix-modal-info">
                <span className={`risk-matrix-modal-badge ${riskService.getSeverityBadgeColor(selectedCell.severity)}`}>
                  {selectedCell.severity} Severity
                </span>
                <span className="risk-matrix-modal-score">
                  Risk Score: {selectedCell.score}
                </span>
              </div>
              <div className="risk-matrix-modal-risks">
                <h5>{selectedCell.risks.length} {selectedCell.risks.length === 1 ? 'Risk' : 'Risks'}</h5>
                <div className="risk-matrix-modal-list">
                  {selectedCell.risks.map(risk => (
                    <div
                      key={risk.id}
                      className="risk-matrix-modal-item"
                      onClick={() => handleRiskItemClick(risk)}
                    >
                      <div className="risk-matrix-modal-item-header">
                        <span className="risk-matrix-modal-item-icon">
                          {riskService.getRiskTypeIcon(risk.risk_type)}
                        </span>
                        <span className="risk-matrix-modal-item-title">{risk.title}</span>
                      </div>
                      <div className="risk-matrix-modal-item-meta">
                        <span className={`risk-matrix-modal-item-badge ${riskService.getRiskTypeColor(risk.risk_type)}`}>
                          {risk.risk_type}
                        </span>
                        <span className={`risk-matrix-modal-item-badge ${riskService.getStatusColor(risk.status)}`}>
                          {risk.status}
                        </span>
                        {risk.project && (
                          <span className="risk-matrix-modal-item-project">
                            {risk.project.name}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskMatrix;

